// @ai-generated 2026-08-27T12:28:10+00:00 by malte.ulfers@tngtech.com using Claude Opus 5
import { UserText } from '@Constants/UserText';
import { useAcceptPartnership, useRejectPartnership } from '@Hooks/Permissions';
import { type ApiUserPartnershipInformation } from '@Model';
import {
    Button,
    ButtonWithConfirmationModal,
    EnumMessageLevel,
    EnumMessageType,
    FlexRow,
    isOk,
    Modal,
    useToasts,
} from '@synqo/frontend-component-library';
import { FC, useCallback } from 'react';

interface AcceptPartnershipProps {
    partnership: ApiUserPartnershipInformation;
    /** Re-read the user information, so the now-accepted partnership becomes usable. */
    refetchUserInformation: () => void;
}

/**
 * Blocks a user that was invited into a partnership until they confirm they want to be in it.
 * Rejecting removes their account from that partnership.
 */
export const AcceptPartnership: FC<AcceptPartnershipProps> = ({ partnership, refetchUserInformation }) => {
    const { pushAllToasts } = useToasts();
    const { mutateAsync: acceptPartnership, isPending: acceptIsPending } = useAcceptPartnership();
    const { mutateAsync: rejectPartnership, isPending: rejectIsPending } = useRejectPartnership();

    const partnershipId = partnership.partnership_id;

    const handleAccept = useCallback(async () => {
        const result = await acceptPartnership(partnershipId);
        if (!isOk(result)) {
            pushAllToasts(result.messages);
            return;
        }
        refetchUserInformation();
    }, [acceptPartnership, partnershipId, pushAllToasts, refetchUserInformation]);

    // Only this partnership is gone; the user may still have others. Re-reading the user information
    // drops back to the selection screen, which handles the case where none is left.
    const handleReject = useCallback(async () => {
        const result = await rejectPartnership(partnershipId);
        if (!isOk(result)) {
            pushAllToasts(result.messages);
            return;
        }
        refetchUserInformation();
    }, [rejectPartnership, partnershipId, pushAllToasts, refetchUserInformation]);

    return (
        <Modal isOpen={true} withoutCloseButton={true} onClose={() => {}} title={'Zugang bestätigen'}>
            <p className={'text-sm'}>
                Sie wurden für den Mandanten <b>{partnership.mandant_display}</b> von{' '}
                <b>{partnership.client_display}</b> in {UserText.APP_NAME} freigeschaltet.
            </p>
            <p className={'text-sm'}>
                Bitte bestätigen Sie, dass Sie diesen Zugang nutzen möchten. Erst danach kann der Mandant Ihr
                Benutzerkonto verwalten.
            </p>

            <FlexRow justify={'space-between'} className={'pt-5'}>
                <ButtonWithConfirmationModal
                    variant={'warning'}
                    appearance={'filled'}
                    onClick={handleReject}
                    locked={acceptIsPending || rejectIsPending}
                    confirmModalMessage={{
                        message:
                            'Wollen Sie den Zugang zu diesem Mandanten wirklich ablehnen? Sie können ihn danach nicht mehr nutzen und müssten erneut eingeladen werden.',
                        title: 'Ablehnen bestätigen',
                        type: EnumMessageType.MultipleChoice,
                        level: EnumMessageLevel.Warning,
                    }}
                    label={'Ablehnen'}
                >
                    {rejectIsPending ? 'Zugang wird abgelehnt...' : 'Ablehnen'}
                </ButtonWithConfirmationModal>
                <Button
                    variant={'success'}
                    appearance={'filled'}
                    onClick={handleAccept}
                    locked={acceptIsPending || rejectIsPending}
                    label={'Zugang bestätigen'}
                >
                    {acceptIsPending ? 'Zugang wird bestätigt...' : 'Zugang bestätigen'}
                </Button>
            </FlexRow>
        </Modal>
    );
};
